import { PDFDocument } from "pdf-lib"
import { useCallback, useState } from "react"

export interface ImageFile {
  id: string
  file: File
  previewUrl: string
  name: string
}

export function useImageToPdf() {
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const convertToPdf = useCallback(async (images: ImageFile[]) => {
    setProcessing(true)
    setProgress(0)
    setError(null)

    try {
      const pdfDoc = await PDFDocument.create()
      const totalImages = images.length

      for (let i = 0; i < images.length; i++) {
        const image = images[i]
        const arrayBuffer = await image.file.arrayBuffer()

        const isJpg =
          image.file.type === "image/jpeg" || image.file.type === "image/jpg"
        const embeddedImage = isJpg
          ? await pdfDoc.embedJpg(arrayBuffer)
          : await pdfDoc.embedPng(arrayBuffer)

        const page = pdfDoc.addPage([embeddedImage.width, embeddedImage.height])
        page.drawImage(embeddedImage, {
          x: 0,
          y: 0,
          width: embeddedImage.width,
          height: embeddedImage.height,
        })

        setProgress(((i + 1) / totalImages) * 100)
      }

      const pdfBytes = await pdfDoc.save()
      return new Blob([pdfBytes as BlobPart], { type: "application/pdf" })
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to convert images to PDF"
      setError(message)
      throw err
    } finally {
      setProcessing(false)
    }
  }, [])

  return { convertToPdf, processing, progress, error }
}
